import {
  Box,
  Checkbox,
  Typography,
  Portal,
  Fab,
  useMediaQuery,
  useTheme,
  Tooltip,
  Paper,
  alpha,
  Container,
} from "@mui/material";
// import Grid from "@mui/material/Grid";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { ExtractedInfoPage } from "../ExtractedInfo/ExtractedInfoPage";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import type { JSONSchema7 } from "json-schema";
import Button from "@mui/material/Button";
import { useToast } from "../../shared/toast";
import { ReportSubmitModal } from "../../modals/ReportSubmitModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React from "react";
import { FieldMapping } from "../../config/fieldMapping";
import { getReportMetadata } from "../../api/reportApi";
import { getCoComments } from "../../api/coReviewApi";
import { rmsClient } from "../../api/clients";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { ResubmitConfirmationDialog } from "../../components/coReview/ResubmitConfirmationDialog";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import { usePageHeader } from "../../context/LayoutContext";
import { useAuthContext } from "../../context/AuthContext";
import { useAuth } from "@workos-inc/authkit-react";
import { useTnCaseNumberInputEnabled } from "../../hooks/useTnCaseNumberInputEnabled";
import { useCoApprovalEnabled } from "../../hooks/useCoApprovalEnabled";
import FactCheckIcon from "@mui/icons-material/FactCheck";

type FinalEditsState = {
  modelData: EforceIncidentModel;
  showRMSCommit: boolean;
  sparseInfo: unknown;
  jsonSchema: JSONSchema7;
  schemaMapping: Record<string, FieldMapping>;
};

export const FinalEditsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reportID } = useParams(); // top-level hook use
  const theme = useTheme();
  // Set page header in app bar
  usePageHeader({
    title: "Final Edits",
    subtitle: "Review and confirm your report before submission",
    icon: <FactCheckIcon />,
  });

  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { organizationId } = useAuthContext();
  const { user } = useAuth();
  const caseNumberInputEnabled = useTnCaseNumberInputEnabled();
  const coApprovalEnabled = useCoApprovalEnabled();

  const [confirmInfo, setConfirmInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [allEasterEggsFound, setAllEasterEggsFound] = useState(false);
  const [reportStatus, setReportStatus] = useState<string>("");
  const [coComments, setCoComments] = useState<CoReviewComment[]>([]);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  // Which action the submit modal confirms. Tracked as explicit state because
  // comparing handleSubmitFunction.current against handleRMSCommit by identity
  // fails — the handler is recreated on every render, so the comparison was
  // always false and the modal showed the "sign" copy on the RMS-commit path.
  const [submitMode, setSubmitMode] = useState<"sign" | "rmsCommit">("sign");
  // Tracks whether the current viewer is the owner of this report. Final Edits
  // is an officer-only signing surface; COs reviewing a colleague's submission
  // are bounced back to the CO review page (or home if reportID is missing).
  const [ownershipCheck, setOwnershipCheck] = useState<
    "pending" | "owner" | "not-owner"
  >("pending");
  const toast = useToast();

  const state = location.state as FinalEditsState | undefined;

  const incidentModelRef = useRef<EforceIncidentModel | null>(
    state?.modelData || null
  );
  const handleSubmitFunction = useRef<(caseNumber?: string) => void>(() => {});

  // References for tracking easter eggs found in each narrative
  const narrativeEasterEggRefs = useRef<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>({});

  /**
   * @name checkAllEasterEggsFound
   *
   * @description Checks if all narratives' easter eggs have been found
   */
  const checkAllEasterEggsFound = () => {
    const model = incidentModelRef.current;
    if (!model || !model.Narratives || model.Narratives.length === 0) {
      setAllEasterEggsFound(true);
      return;
    }

    const refs = Object.values(narrativeEasterEggRefs.current);
    const expectedCount = model.Narratives.length;
    const allFound =
      refs.length === expectedCount &&
      refs.every((ref) => ref?.current === true);

    setAllEasterEggsFound(allFound);
  };

  /**
   * @name handleEasterEggFound
   *
   * @description Callback when an easter egg is found - rechecks if all are found
   */
  const handleEasterEggFound = () => {
    checkAllEasterEggsFound();
  };

  // Check if there are any narratives when component mounts
  React.useEffect(() => {
    checkAllEasterEggsFound();
  }, []);

  // Fetch metadata + CO comments so we can decide whether this is a resubmission.
  // CO comments are only fetched when the workflow is enabled — otherwise
  // listComments returns 403 and rejects the whole Promise.all, locking the
  // owner out of their own final-edits page.
  useEffect(() => {
    if (!reportID) return;
    let cancelled = false;
    const commentsPromise = coApprovalEnabled
      ? getCoComments(reportID, { cycle: "current" })
      : Promise.resolve<CoReviewComment[]>([]);
    Promise.all([getReportMetadata(reportID), commentsPromise])
      .then(([meta, fetched]) => {
        if (cancelled) return;
        setReportStatus(meta.reportStatus);
        setCoComments(fetched);
        // Final Edits is the officer's signing surface; bounce anyone else
        // (e.g. a CO who navigated here from the review queue) back to the
        // CO review page.
        if (user?.id && meta.userId && user.id !== meta.userId) {
          setOwnershipCheck("not-owner");
        } else {
          setOwnershipCheck("owner");
        }
      })
      .catch(() => {
        // Fail closed: if we can't verify ownership, treat the viewer as a
        // non-owner so the officer-only signing CTA stays hidden.
        if (cancelled) return;
        setOwnershipCheck("not-owner");
      });
    return () => {
      cancelled = true;
    };
  }, [reportID, user?.id, coApprovalEnabled]);

  // Hooks must run on every render before any conditional early-returns
  // below (`hasRequiredState`, `ownershipCheck === "pending" | "not-owner"`).
  // Moving these useMemos above the gates keeps the hook order stable.
  const rejectedSectionLabels = useMemo(
    () =>
      Array.from(
        new Set(
          coComments
            .filter((c) => c.isSectionRejection && c.fieldLabel)
            .map((c) => c.fieldLabel as string),
        ),
      ),
    [coComments],
  );
  const unresolvedCount = useMemo(
    () =>
      coComments.filter((c) => !c.parentCommentId && !c.resolvedAt).length,
    [coComments],
  );
  const memoizedSchemaMapping = useMemo(() => {
    // update schema mapping to have NarrativeViewWidget for narratives
    // Deep clone to avoid mutating the original schema mapping. `state` may be
    // undefined on renders before the !hasRequiredState redirect fires below;
    // bail out with null so this hook still runs and the redirect still wins.
    if (!state?.schemaMapping) return null;
    let newSchemaMapping = JSON.parse(JSON.stringify(state.schemaMapping));
    if (newSchemaMapping["Narratives"]?.properties?.["Text"]) {
      // schema mapping for eforce
      newSchemaMapping["Narratives"].properties["Text"].widget =
        "NarrativeViewWidget";
    } else if (newSchemaMapping["Narratives"]?.properties?.["Narrative"]) {
      // fallback schema mapping for IncidentUS (genericSchemaMapping)
      newSchemaMapping["Narratives"].properties["Narrative"].widget =
        "NarrativeViewWidget";
    }
    return newSchemaMapping;
  }, [state?.schemaMapping]);

  const hasRequiredState =
    !!state?.modelData &&
    typeof state?.showRMSCommit === "boolean" &&
    !!state?.sparseInfo;

  // Redirect immediately (before rendering the rest) if state is missing
  if (!hasRequiredState) {
    // Replace with your actual edit route; using /edit here as an example
    const to = `/edit-section/${reportID ?? "/"}`;
    return (
      <Navigate to={to} replace state={{ error: "No data was provided" }} />
    );
  }

  // Don't render the signing UI until ownership has been verified — otherwise
  // a non-owner briefly sees the officer-only sign CTA between mount and the
  // metadata fetch resolving.
  if (ownershipCheck === "pending") return null;

  // Non-owners (typically COs) can't access the officer's signing surface.
  // Route them to the CO review page if a reportID is available, otherwise home.
  if (ownershipCheck === "not-owner") {
    const to = reportID ? `/co-review/${reportID}` : "/";
    return <Navigate to={to} replace />;
  }

  const handleConfirmCheckboxClick = () => {
    if (!allEasterEggsFound) {
      toast.error(
        "Please find and delete all easter eggs in the narratives before you can confirm and submit the report."
      );
      return;
    }
    setConfirmInfo((prev) => !prev);
  };

  // Legacy direct-to-RMS path. Used only when the CO workflow flag is off —
  // when on, the CO performs the commit from the review page after approval.
  const handleRMSCommit = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    if (!reportID) {
      toast.error("Report ID is missing.");
      return;
    }
    const incident = incidentModelRef.current;
    if (!incident) {
      toast.error("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      const res = await rmsClient.post("/report/rms-commit", {
        reportId: parseInt(reportID),
        ...(caseNumber ? { caseNumber } : {}),
      });
      const eventNumber = res.data.eventNumber;
      navigate("/confirmation/" + eventNumber, {
        state: { reportID },
      });
    } catch (err) {
      console.error(err);
      toast.error(
        "An error occurred while submitting the report to the RMS servers. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitClick = (
    submitFunction: (caseNumber?: string) => void,
    mode: "sign" | "rmsCommit" = "sign",
  ) => {
    if (isLoading) return;
    handleSubmitFunction.current = submitFunction;
    setSubmitMode(mode);
    // Spec §5: when resubmitting after a CO rejection, ask the officer to confirm
    // they've addressed the comments before opening the standard submit confirmation.
    if (reportStatus === ReportStatus.CHANGES_REQUESTED) {
      setResubmitDialogOpen(true);
      return;
    }
    setOpenConfirmation(true);
  };

  const handleResubmitConfirm = () => {
    setResubmitDialogOpen(false);
    setOpenConfirmation(true);
  };

  const handleSignReport = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    if (!reportID) {
      toast.error("Report ID is missing.");
      return;
    }
    const incident = incidentModelRef.current;
    if (!incident) {
      toast.error("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      await rmsClient.post("/report/sign", {
        reportId: reportID,
        ...(caseNumber ? { caseNumber } : {}),
      });
      navigate("/confirmation", {
        state: {
          reportID,
          // Only the CO workflow holds the report for review; the legacy path
          // signs and makes it downloadable immediately (default page copy).
          ...(coApprovalEnabled
            ? {
                message:
                  "Your report is signed and awaiting Commanding Officer review. You will be notified once it has been reviewed.",
              }
            : {}),
        },
      });
    } catch (err) {
      console.error(err);
      toast.error(
        "An error occurred while signing the report. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isRMSCommit = submitMode === "rmsCommit";

  return (
    <>
      {/* Page Header Removed */}
      {/* <Container ... /> */}

      <Portal>
        <Tooltip title="Return to editing page">
          <Fab
            variant={isLgUp ? "extended" : "circular"}
            onClick={() => navigate(-1)}
            disabled={isLoading}
            sx={{
              position: "fixed",
              top: isLgUp ? 70 : 130,
              right: 16,
              zIndex: 1000,
              bgcolor: theme.palette.primary.main,
              color: "white",
              border: "2px solid white",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            <ArrowBackIcon />
            {isLgUp && "Back to Edit"}
          </Fab>
        </Tooltip>
      </Portal>

      <ReportSubmitModal
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleSubmitReport={handleSubmitFunction.current}
        isRMSCommit={isRMSCommit}
        requireCaseNumber={caseNumberInputEnabled}
      />
      <ResubmitConfirmationDialog
        open={resubmitDialogOpen}
        totalComments={
          coComments.filter((c) => !c.parentCommentId).length
        }
        unresolvedComments={unresolvedCount}
        rejectedSectionLabels={rejectedSectionLabels}
        onClose={() => setResubmitDialogOpen(false)}
        onConfirm={handleResubmitConfirm}
        loading={isLoading}
      />

      {/* Easter Egg Warning Banner */}
      {!allEasterEggsFound && (
        <Container maxWidth="xl" sx={{ mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.5rem",
                lineHeight: 1,
              }}
            >
              ⚠️
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.warning.dark,
                  mb: 0.5,
                }}
              >
                Action Required: Find and Remove Easter Eggs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Before you can confirm and submit this report, you must find and
                delete the hidden "easter egg" phrases in each narrative
                section. These are nonsensical phrases highlighted in orange.
                Click on the easter egg text and press{" "}
                <strong>Backspace</strong> or <strong>Delete</strong> to remove
                it.
              </Typography>
            </Box>
          </Paper>
        </Container>
      )}

      <ExtractedInfoPage
        isFinalReview
        incidentModelRef={incidentModelRef}
        narrativeEasterEggRefs={narrativeEasterEggRefs}
        onEasterEggFound={handleEasterEggFound}
        jsonSchema={state.jsonSchema}
        schemaMapping={memoizedSchemaMapping}
        stationId={organizationId ?? undefined}
      />

      {/* Confirmation and Submit Section */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Confirmation Checkbox */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              cursor: !allEasterEggsFound ? "not-allowed" : "pointer",
              p: 2,
              borderRadius: 2,
              bgcolor: confirmInfo
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${
                confirmInfo
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.divider, 0.1)
              }`,
              transition: "all 0.2s ease-in-out",
              width: "100%",
              maxWidth: 700,
              "&:hover": {
                bgcolor: confirmInfo
                  ? alpha(theme.palette.success.main, 0.12)
                  : alpha(theme.palette.primary.main, 0.08),
              },
            }}
            onClick={() => {
              if (!allEasterEggsFound) {
                toast.error(
                  "Please find and delete all easter eggs in the narratives before you can confirm and submit the report."
                );
              }
            }}
          >
            <Checkbox
              checked={confirmInfo}
              onChange={handleConfirmCheckboxClick}
              disabled={!allEasterEggsFound}
              sx={{
                color: theme.palette.primary.main,
                "&.Mui-checked": {
                  color: theme.palette.success.main,
                },
                "& .MuiSvgIcon-root": {
                  fontSize: 28,
                },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 500,
                color: confirmInfo
                  ? theme.palette.success.dark
                  : theme.palette.text.primary,
              }}
            >
              I confirm that all the information on this page is accurate to my
              knowledge.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 4,
              width: "100%",
            }}
          >
            {coApprovalEnabled ? (
              <>
                <Button
                  onClick={() => handleSubmitClick(handleSignReport)}
                  variant="contained"
                  disabled={
                    !confirmInfo ||
                    isLoading ||
                    !incidentModelRef.current ||
                    !allEasterEggsFound
                  }
                  loading={isLoading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.35,
                    )}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(
                        theme.palette.primary.main,
                        0.45,
                      )}`,
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                      boxShadow: "none",
                    },
                  }}
                >
                  {reportStatus === ReportStatus.CHANGES_REQUESTED
                    ? "Resubmit for CO Review"
                    : "Sign & Send to CO Review"}
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    maxWidth: 520,
                  }}
                >
                  Your Commanding Officer will review the report, leave any
                  comments, and either approve it for RMS commit or send it
                  back for changes.
                </Typography>
              </>
            ) : (
              <>
                {state.showRMSCommit && (
                  <>
                    <Button
                      onClick={() => handleSubmitClick(handleRMSCommit, "rmsCommit")}
                      variant="contained"
                      disabled={
                        !confirmInfo ||
                        isLoading ||
                        !incidentModelRef.current ||
                        !allEasterEggsFound
                      }
                      loading={isLoading}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        boxShadow: `0 4px 14px ${alpha(
                          theme.palette.primary.main,
                          0.35,
                        )}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: `0 6px 20px ${alpha(
                            theme.palette.primary.main,
                            0.45,
                          )}`,
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: theme.palette.grey[300],
                          color: theme.palette.grey[500],
                          boxShadow: "none",
                        },
                      }}
                    >
                      Sign Report & Commit to RMS
                    </Button>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      or
                    </Typography>
                  </>
                )}
                <Button
                  onClick={() => handleSubmitClick(handleSignReport)}
                  variant={state.showRMSCommit ? "outlined" : "contained"}
                  disabled={
                    !confirmInfo ||
                    isLoading ||
                    !incidentModelRef.current ||
                    !allEasterEggsFound
                  }
                  loading={isLoading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    ...(state.showRMSCommit
                      ? {
                          borderWidth: 2,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            borderWidth: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }
                      : {
                          boxShadow: `0 4px 14px ${alpha(
                            theme.palette.primary.main,
                            0.35,
                          )}`,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: `0 6px 20px ${alpha(
                              theme.palette.primary.main,
                              0.45,
                            )}`,
                            transform: "translateY(-2px)",
                          },
                        }),
                    "&:disabled": {
                      background: state.showRMSCommit
                        ? "transparent"
                        : theme.palette.grey[300],
                      color: theme.palette.grey[500],
                      boxShadow: "none",
                      borderColor: theme.palette.grey[300],
                    },
                  }}
                >
                  Sign Report & Download
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  );
};
